/**
 * RDS cutover call sites -- TEST_INTEGRATION_PLAN.md §5 (Phase 3) and §6
 * (Phase 4, the same pattern repeated one call site at a time):
 *
 *   patient_list     PatientListPage.js roster query (Phase 3 pilot):
 *                    serveRosterFromRds / startRosterFromRds.
 *   patient_suggest  PatientSuggestionDropdown.js roster name search
 *                    (Phase 4, site 2): serveSuggestionsFromRds /
 *                    startSuggestionsFromRds.
 *   patient_table    PatientTableViewer.js whole-roster view (Phase 4,
 *                    site 3): servePatientTableFromRds /
 *                    startPatientTableFromRds.
 *   roster_visits    rosterVisitLookup.js notes-c visit counts for the
 *                    roster page (Phase 4, site 4): serveVisitCountsFromRds /
 *                    startVisitCountsFromRds.
 *   latest_visit     PatientListPage.openLatestVisit one-row lookup
 *                    (Phase 4, site 5): serveLatestVisitFromRds /
 *                    startLatestVisitFromRds.
 *   prior_visit_match priorVisitMatch.js earlier-visit list (Phase 4,
 *                    site 6): one /query call per key in {patient_name,
 *                    EHR_ID} (the same shape the Supabase read issues),
 *                    date_ lt before, date_ desc, limit 30; columns
 *                    id + EHR_ID + patient_name + date_ + type.
 *   suggestion_visits PatientSuggestionDropdown visits half (Phase 4,
 *                    site 7): two /query calls per schema -- (a)
 *                    `ilike patient_name, date_ lt refDate, date_ desc,
 *                    limit 15`, (b) `ilike patient_name, any-dates
 *                    nulls-last, limit 15`. Same projection the Supabase
 *                    read selects.
 *   search_visits    SearchComponent.js visits query (Phase 4, site 8):
 *                    one ILIKE per typed term (name fields per word),
 *                    date_ eq / gte+lte, date_ desc, 1000 cap.
 *   search_roster    SearchComponent.js roster query (Phase 4, site 8):
 *                    one ILIKE per typed term, patient_name asc nulls last.
 *
 * Every site follows the one contract below; only the flag name, the
 * query body and the reporting pilot differ (see SITES).
 *
 * The story, per schema, when a site's cutover is effective:
 *   1. The Supabase query still runs exactly as today (DUAL-READ). Its
 *      rows are never thrown away: they are (a) the parity oracle the
 *      full comparison below is measured against and (b) the instant
 *      fallback if RDS fails.
 *   2. RDS is asked the same question (rdsPhi.queryRows, hard timeout).
 *      If it answers, ITS rows are what the page shows -- RDS's
 *      row-level authorization (rds_access.resolve) is therefore the
 *      real thing now, not a shadow: rows the viewer is not entitled to
 *      are simply not there, and masked_count says how many.
 *   3. If RDS errors, times out, or is unreachable, the page keeps the
 *      Supabase rows it already has (`served: "supabase_fallback"`) and
 *      the user sees nothing different. The failure is reported.
 *   4. Either way the OUTCOME (counts + column names only, never row
 *      content) is POSTed to /api/rds-shadow-report/ with
 *      mode: "cutover", so the admin dashboard shows what was actually
 *      served and how well RDS agreed with Supabase.
 *
 * Supabase remains the parity oracle for WHAT an entitled row contains
 * (matched / mismatched), not for WHO may see it -- its RLS returns
 * every row to every authenticated user (rdsShadowRead.js,
 * TEST_INTEGRATION_PLAN.md §11), so `missing` (in Supabase, not RDS) is
 * expected to include exactly the rows RDS withheld.
 *
 * Two switches must BOTH be on before any of this runs, so no existing
 * deploy changes behaviour:
 *   - an RDS_API_URL on the backend (cutoverBuildEnabled): the
 *     frontend does not even ask RDS about flags without one;
 *   - RDS's own per-call-site flag, and only its `effective` value
 *     (enabled AND no kill switch AND fresh mirror, decided server-side).
 * Rollback is instant and needs no deploy: POST /admin/cutover
 * {enabled:false} on RDS, or RDS_CUTOVER_DISABLED=1 in its env; the
 * next roster fetch (flags are cached 30s) is back on Supabase. Unsetting
 * RDS_API_URL is the belt-and-braces third lever. When not effective
 * the page's behaviour is byte-for-byte today's.
 */
import { getSupabase } from "../components/supabaseClient";
import { getBackendURL } from "../components/env";
import { containsFilterTerms } from "./patientNameSearch";
import {
  queryRows, getCutoverFlags, getSourceMode, writeRow, PhiClientError, PhiClientUnavailable,
  rdsBaseUrl,
} from "./rdsPhi";

const CUTOVER_PILOT = "patient_list";
const ROSTER_TABLE = "patients_with_ehrid";
// Mirrors PatientListPage's FETCH_CAP: the page passes its own value in,
// this is only the default for direct callers.
const ROSTER_FETCH_CAP = 1000;
// The same three columns PatientListPage's or() filter searches --
// parity requires the two sides to ask the same question.
const ROSTER_SEARCH_COLUMNS = ["patient_name", "ehr_id", "email"];
// RDS refuses a search value longer than this (400); rather than pay a
// round trip for a guaranteed error, such a search stays on Supabase.
const ROSTER_SEARCH_MAX_LEN = 200;

// Phase 4, site 2: PatientSuggestionDropdown.js's roster half. The
// dropdown ILIKEs patient_name only and takes 10 rows per tenant; RDS is
// asked the very same question (same column, same cap, same sort) so the
// full-set comparison is meaningful.
const SUGGEST_PILOT = "patient_suggest";
const SUGGEST_LIMIT = 10;
const SUGGEST_SEARCH_COLUMNS = ["patient_name"];

// One entry per cutover call site: the RDS flag it is gated on (also the
// dashboard pilot it reports under -- the two names are kept identical
// on purpose, see backend/api/rds_dashboard.py _ALLOWED_CLIENT_PILOTS)
// and the mirrored table it reads.
// Phase 4, site 3: PatientTableViewer.js (SuperAdmin's patient-table
// modal) reads the whole roster newest first with no explicit cap --
// PostgREST's default max-rows (1000 on Supabase) is the real one. RDS is
// asked for the same 1000 (its /query ceiling) so the two sides agree up
// to that cap; beyond it RDS says `truncated`, exactly like the roster.
const TABLE_PILOT = "patient_table";
const TABLE_FETCH_CAP = 1000;

// Phase 4, site 4: rosterVisitLookup.js answers "has this roster patient an
// appointment?" from notes-c -- one `vita_id IN (<= 200 ids)` read per
// chunk, id + vita_id only. RDS is asked the same question through the
// /query filters + columns extension (TEST_INTEGRATION_PLAN.md §17):
// `id` keeps the full-set comparison row-by-row, `vita_id` is what the
// counts are keyed by. The 1000 cap is PostgREST's default max-rows, the
// cap the Supabase read has always had without saying so.
const VISITS_PILOT = "roster_visits";
const VISITS_TABLE = "notes-c";
const VISITS_CHUNK = 200;
const VISITS_FETCH_CAP = 1000;

// Phase 4, site 5: PatientListPage.openLatestVisit reads notes-c for the
// chooser's "Latest visit" click. One row (limit 1), keyed by vita_id, newest
// first -- the same projection the Supabase read selected (id, date_,
// patient_name, EHR_ID). RDS's access_level rides along for the audit / row-by-row
// comparison; the route strips it.
const LATEST_VISIT_PILOT = "latest_visit";
const LATEST_VISIT_TABLE = "notes-c";
const LATEST_VISIT_COLUMNS = ["id", "date_", "patient_name", "EHR_ID"];

// Phase 4, site 6: priorVisitMatch.js issues one Supabase query per key
// (patient_name and/or EHR_ID when usable), each with date_ lt <before>.
// RDS sees the same question per call -- the call-site builds the keys
// array and calls this once per key -- so the SITES entry is one row.
const PRIOR_VISIT_PILOT = "prior_visit_match";
const PRIOR_VISIT_TABLE = "notes-c";
const PRIOR_VISIT_COLUMNS = ["id", "EHR_ID", "patient_name", "date_", "type"];
const PRIOR_VISIT_FETCH_CAP = 30;

// Phase 4, site 7: PatientSuggestionDropdown visits half. Two queries
// per schema (past + any), each dual-read. The dropdown selects "*"; the
// projection below is the superset those rows actually need.
const SUGGEST_VISITS_PILOT = "suggestion_visits";
const SUGGEST_VISITS_TABLE = "notes-c";
const SUGGEST_VISITS_LIMIT = 15;
const SUGGEST_VISITS_COLUMNS = [
  "id", "date_", "patient_name", "EHR_ID", "type", "vita_id",
  "user_id", "shared_users", "source_record_ids",
];

// Phase 4, site 8: SearchComponent.js. Two tables, two flags. Both
// Supabase reads pass no limit (PostgREST's default 1000 applies) and
// build one ILIKE per typed term -- name fields split per word (see
// patientNameSearch.js), ANDed. /query takes at most 8 filters; a search
// needing more stays on Supabase (`too_many_filters`), never truncated.
const SEARCH_VISITS_PILOT = "search_visits";
const SEARCH_ROSTER_PILOT = "search_roster";

// Phase 4, site 9: usePatientDocument.js reads ONE chart header row by key
// -- the roster copy (patients_with_ehrid, id) or the visit (notes-c, id
// or EHR_ID). Supabase's .single() refuses two matches; RDS is asked for
// up to two so the same ambiguity is visible to the caller.
const CHART_ROSTER_PILOT = "chart_roster";
const CHART_VISIT_PILOT = "chart_visit";
const CHART_VISIT_KEY_COLUMNS = ["id", "EHR_ID"];
// Phase 4, site 10: PriorVisitsModal.js history list.
const PRIOR_VISITS_MODAL_PILOT = "prior_visits_modal";
const PRIOR_VISITS_MODAL_LIMIT = 200;
const PRIOR_VISITS_MODAL_COLUMNS = [
  "id", "EHR_ID", "patient_name", "date_", "time_", "type", "template",
  "notes_behavior", "transcripts", "more_audios",
];
// Phase 4, site 11: SuperAdmin.js patients modal, the whole `patients`
// table with PostgREST's default 1000 cap.
const ADMIN_PATIENTS_PILOT = "admin_patients";
const ADMIN_PATIENTS_TABLE = "patients";
const ADMIN_PATIENTS_FETCH_CAP = 1000;
const SEARCH_FETCH_CAP = 1000;
const SEARCH_MAX_FILTERS = 8;

const SITES = {
  [CUTOVER_PILOT]: { table: ROSTER_TABLE },
  [SUGGEST_PILOT]: { table: ROSTER_TABLE },
  [TABLE_PILOT]: { table: ROSTER_TABLE },
  [VISITS_PILOT]: { table: VISITS_TABLE },
  [LATEST_VISIT_PILOT]: { table: LATEST_VISIT_TABLE },
  [PRIOR_VISIT_PILOT]: { table: PRIOR_VISIT_TABLE },
  [SUGGEST_VISITS_PILOT]: { table: SUGGEST_VISITS_TABLE },
  [SEARCH_VISITS_PILOT]: { table: VISITS_TABLE },
  [SEARCH_ROSTER_PILOT]: { table: ROSTER_TABLE },
  // Phase 4, sites 9-11 (see the sections at the end of this file).
  [CHART_ROSTER_PILOT]: { table: ROSTER_TABLE },
  [CHART_VISIT_PILOT]: { table: VISITS_TABLE },
  [PRIOR_VISITS_MODAL_PILOT]: { table: VISITS_TABLE },
  [ADMIN_PATIENTS_PILOT]: { table: ADMIN_PATIENTS_TABLE },
};

// Circuit breaker. A hung RDS costs the full query timeout per schema per
// fetch; once one attempt times out or cannot connect, every fetch for the
// next COOLDOWN_MS goes straight to the Supabase rows (and says so). A
// definite answer from RDS (any HTTP status) does not trip it.
const RDS_COOLDOWN_MS = 30000;
let rdsCooldownUntil = 0;

/** Test hook -- forget an open circuit. */
export function _resetCutoverCircuit() {
  rdsCooldownUntil = 0;
}

function circuitOpen() {
  return Date.now() < rdsCooldownUntil;
}

function tripCircuit() {
  rdsCooldownUntil = Date.now() + RDS_COOLDOWN_MS;
}

/** The shared breaker, for the overlays that live in their own modules
 * (rdsStorageOverlay.js): one hung RDS answer anywhere skips every RDS
 * attempt for the cooldown. */
export function rdsCircuitOpen() {
  return circuitOpen();
}

export function tripRdsCircuit() {
  tripCircuit();
}

/** Short, PHI-free label for why RDS could not be used. */
function unavailableReason(err) {
  const msg = String(err?.message || "");
  if (msg.includes("timed out")) return "timeout";
  if (msg.includes("aborted")) return "aborted";
  if (msg.includes("no active session")) return "no_session";
  return "unreachable";
}

/**
 * Does this deployment use RDS at all? True when the backend has an
 * RDS_API_URL (served by /api/config/). Everything finer -- pausing RDS
 * here, Supabase-only, per-table flags, the kill switch -- is decided by
 * the operator controls through GET /cutover, not by an env variable.
 */
export function cutoverBuildEnabled() {
  return !!rdsBaseUrl();
}

/**
 * Should `callSite` be served from RDS right now? False unless the build
 * flag is on AND RDS reports the flag as `effective` (never merely
 * `enabled` -- the kill switch and mirror freshness live inside
 * `effective`). Never throws: getCutoverFlags fails closed, and anything
 * unexpected here is treated the same way.
 */
export async function isCutoverEffective(callSite) {
  if (!cutoverBuildEnabled()) return false;
  if (!callSite) return false;
  try {
    const payload = await getCutoverFlags();
    return payload?.flags?.[callSite]?.effective === true;
  } catch (_) {
    return false;
  }
}

/**
 * Is RDS the source of truth for PHI right now (TEST_INTEGRATION_PLAN.md
 * §31)? True only when the build switch is on AND RDS's last known source
 * mode is "independent". Unlike isCutoverEffective this does NOT fail
 * closed on a flag-fetch failure: getSourceMode falls back to the sticky
 * mode it remembered, because once the whole-table mirror has stopped a
 * write the overlay fails to forward is not re-synced by anything else
 * (§31 data #3). Never throws.
 */
export async function isIndependent() {
  if (!cutoverBuildEnabled()) return false;
  try {
    return (await getSourceMode()) === "independent";
  } catch (_) {
    return false;
  }
}

// Keys that never take part in the comparison: RDS's own access_level
// annotation, and the page's underscore-prefixed client annotations
// (_schema, _hasVisits, ...). Same rule as rowsRoughlyMatch in
// rdsShadowRead.js, spelled out for the full-set comparison.
function isIgnoredKey(k) {
  return k === "access_level" || k.startsWith("_");
}

function rowsRoughlyMatch(rdsRow, supabaseRow) {
  if (!rdsRow || !supabaseRow) return false;
  const commonKeys = Object.keys(rdsRow).filter(
    (k) => !isIgnoredKey(k) && Object.prototype.hasOwnProperty.call(supabaseRow, k),
  );
  return commonKeys.every(
    (k) => JSON.stringify(rdsRow[k]) === JSON.stringify(supabaseRow[k]),
  );
}

/**
 * Full-set comparison of RDS's roster against Supabase's, keyed by
 * String(id). Unlike the sampled shadow compare this covers EVERY row
 * both sides returned, because in cutover mode RDS's rows are what the
 * user is looking at.
 *
 * @returns {{matched: number, mismatched: number, missing: number,
 *   extra: number, columns: string[]}} missing = in Supabase but not in
 *   RDS (includes rows RDS withheld as not entitled); extra = in RDS but
 *   not in Supabase (a mirror that is ahead of / diverged from the
 *   source); columns = the RDS column NAMES seen (never values).
 */
export function compareRosterSets(rdsRows, supabaseRows) {
  const rdsById = {};
  (rdsRows || []).forEach((r) => { if (r?.id != null) rdsById[String(r.id)] = r; });
  const supabaseById = {};
  (supabaseRows || []).forEach((r) => { if (r?.id != null) supabaseById[String(r.id)] = r; });

  let matched = 0;
  let mismatched = 0;
  let missing = 0;
  let extra = 0;
  const columnsSeen = new Set();
  Object.keys(supabaseById).forEach((id) => {
    const rdsRow = rdsById[id];
    if (!rdsRow) { missing += 1; return; }
    if (rowsRoughlyMatch(rdsRow, supabaseById[id])) matched += 1;
    else mismatched += 1;
  });
  Object.keys(rdsById).forEach((id) => {
    if (!supabaseById[id]) extra += 1;
    Object.keys(rdsById[id]).forEach((k) => {
      if (!isIgnoredKey(k)) columnsSeen.add(k);
    });
  });
  return {
    matched, mismatched, missing, extra, columns: Array.from(columnsSeen).sort(),
  };
}

/**
 * Report one cutover outcome to the admin dashboard
 * (POST /api/rds-shadow-report/, mode "cutover"). `fields.pilot` names
 * the call site (default "patient_list", the roster).
 * Counts, column names and error classes only -- never row content.
 * Never throws and the page never awaits it: like rdsShadowRead's
 * reportOutcome this is diagnostic telemetry and must not be able to
 * add latency or fail the roster. Returns a promise that always
 * resolves so tests can flush it.
 */
export async function reportCutoverOutcome(fields) {
  try {
    const supabase = await getSupabase();
    const { data: { session } = {} } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return;
    await fetch(`${getBackendURL()}/api/rds-shadow-report/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ pilot: CUTOVER_PILOT, mode: "cutover", ...fields }),
    });
  } catch (_) {
    // Reporting failures must never surface anywhere.
  }
}

/**
 * Kick off one call site's RDS query for one schema WITHOUT waiting for
 * it. The caller starts this right next to its Supabase query so the two
 * round trips overlap instead of adding up; `serveFromRds` then settles
 * it once the Supabase rows (oracle + fallback) are in hand.
 *
 * Resolves to {result} or {error} -- never rejects -- or to a local
 * decision ({skip, reason}) when RDS is not even asked: the circuit is
 * open, or the search term is one RDS is guaranteed to refuse.
 *
 * @param {string} site a SITES key.
 * @param {string} schema tenant schema.
 * @param {object} body the /query body, already shaped like the Supabase
 *   query it mirrors; `body.search.value` is length-checked here.
 * @param {AbortSignal} [signal] aborts the RDS request.
 */
function startFromRds(site, schema, body, signal) {
  if (circuitOpen()) {
    return Promise.resolve({ skip: true, reason: "circuit_open" });
  }
  if (body.search && body.search.value.length > ROSTER_SEARCH_MAX_LEN) {
    return Promise.resolve({ skip: true, reason: "search_too_long" });
  }
  return queryRows(schema, SITES[site].table, body, { signal })
    .then((result) => ({ result }))
    .catch((error) => ({ error }));
}

/**
 * Serve one schema's rows for one call site from RDS, with Supabase's
 * already-fetched rows as the oracle and the fallback. Resolves -- never
 * rejects -- to either
 *   { rows, truncated, served: "rds" }               RDS answered; rows
 *     have access_level stripped (the page never shows or persists it),
 *     truncated is RDS's own "page was full" signal so the caller can
 *     surface it exactly as it does for a full Supabase fetch; or
 *   { rows: supabaseRows, truncated: false, served: "supabase_fallback",
 *     reason }                                        RDS was not used;
 *     the caller carries on with what it already had.
 * Each path reports its outcome first (fire-and-forget) under the site's
 * pilot name.
 *
 * Falls back, besides on any error, when RDS answers with NO rows and NO
 * withheld rows while Supabase has some: withheld rows are always
 * counted, so "nothing at all" means RDS simply does not hold this
 * schema's data (a mirror gap), never an authorization decision -- a
 * blank result would be wrong. The opposite case (every row withheld) IS
 * served as RDS decided, but reported as `all_withheld` so the dashboard
 * can tell it from a clean match.
 *
 * @param {string} site a SITES key.
 * @param {string} schema tenant schema the Supabase query ran against.
 * @param {object} opts
 * @param {object[]} [opts.supabaseRows] the rows Supabase returned for
 *   this schema (raw, before any client-side annotation).
 * @param {Promise} opts.started the startFromRds() promise for this
 *   schema (begun concurrently with the Supabase query).
 */
async function serveFromRds(site, schema, { supabaseRows = [], started }) {
  const base = {
    pilot: site, tenant: schema, table: SITES[site].table, sample_size: supabaseRows.length,
  };
  const fallback = (reason, fields) => {
    reportCutoverOutcome({ ...base, served: "supabase_fallback", reason, ...fields });
    return { rows: supabaseRows, truncated: false, served: "supabase_fallback", reason };
  };

  let settled;
  try {
    settled = await started;
  } catch (err) {
    settled = { error: err };
  }
  if (settled?.skip) {
    return fallback(settled.reason, { outcome: "skipped" });
  }
  if (settled?.error !== undefined) {
    const err = settled.error;
    let outcome;
    let reason;
    const extra = {};
    if (err instanceof PhiClientError) {
      // 404 = RDS holds no copy of this schema's table: not an error in
      // RDS, a mirror gap the dashboard should show as such.
      outcome = err.statusCode === 404 ? "rds_not_mirrored" : "rds_error";
      reason = `PhiClientError ${err.statusCode}`;
      extra.status_code = err.statusCode;
      // FastAPI's 422 detail is a list that echoes the offending input
      // (which for a search is a patient name) -- only a plain string
      // detail is ever forwarded to the dashboard.
      extra.detail = typeof err.detail === "string" ? err.detail : "non-string detail omitted";
    } else if (err instanceof PhiClientUnavailable) {
      outcome = "rds_unavailable";
      reason = unavailableReason(err);
      // A definite refusal (no session, or our own abort) is not RDS
      // being down; a timeout or a failed connection is.
      if (reason === "timeout" || reason === "unreachable") tripCircuit();
    } else {
      outcome = "unexpected_error";
      reason = err?.name || "Error";
    }
    return fallback(reason, { outcome, ...extra });
  }

  try {
    const result = settled?.result || {};
    const rdsRows = (Array.isArray(result.rows) ? result.rows : []).filter(
      (r) => r && typeof r === "object",
    );
    const masked = Number.isFinite(result.masked_count) ? result.masked_count : 0;
    const comparison = compareRosterSets(rdsRows, supabaseRows);
    if (rdsRows.length === 0 && masked === 0 && supabaseRows.length > 0) {
      return fallback("rds_empty", { outcome: "rds_empty", ...comparison, masked });
    }
    const rows = rdsRows.map(({ access_level, ...rest }) => rest);
    reportCutoverOutcome({
      ...base,
      served: "rds",
      ...comparison,
      masked,
      outcome: rdsRows.length === 0 && masked > 0 && supabaseRows.length > 0 ? "all_withheld" : "ok",
    });
    return { rows, truncated: result.truncated === true, served: "rds" };
  } catch (err) {
    // Shaping RDS's answer failed after the fact: still the Supabase rows,
    // never a rejected promise into the caller's error path.
    return fallback(err?.name || "Error", { outcome: "unexpected_error" });
  }
}

// -- patient_list: PatientListPage.js roster query (Phase 3) ------------

/**
 * Kick off the RDS roster query for one schema WITHOUT waiting for it
 * (see startFromRds). `serveRosterFromRds` settles it.
 *
 * @param {string} schema tenant schema.
 * @param {object} opts
 * @param {string} [opts.search] the page's already-sanitised search term.
 * @param {number} [opts.limit] page cap (PatientListPage's FETCH_CAP).
 * @param {AbortSignal} [opts.signal] aborts the RDS request (a superseded
 *   fetch generation).
 */
export function startRosterFromRds(schema, { search = "", limit = ROSTER_FETCH_CAP, signal } = {}) {
  const term = String(search || "").trim();
  // Byte-for-byte the Supabase query's shape: same cap, same sort
  // (patient_name asc, nulls last), same three ILIKE columns, and no
  // include_masked -- withheld rows stay withheld. A whitespace-only term
  // (the page strips `%,()` to spaces) means "whole roster" on both sides.
  const body = {
    limit,
    order: { column: "patient_name", ascending: true, nulls_first: false },
  };
  if (term) body.search = { columns: ROSTER_SEARCH_COLUMNS, value: term };
  return startFromRds(CUTOVER_PILOT, schema, body, signal);
}

/**
 * Serve one schema's roster from RDS (see serveFromRds for the contract).
 *
 * @param {string} schema tenant schema the Supabase query ran against.
 * @param {object} opts
 * @param {string} [opts.search] the page's already-sanitised search term
 *   (`q`); empty/absent means "whole roster". Passed to RDS as-is so
 *   both sides ILIKE the same value.
 * @param {object[]} [opts.supabaseRows] the rows Supabase returned for
 *   this schema (raw, before the page's _schema/_hasVisits annotation).
 * @param {number} [opts.limit] page cap, i.e. PatientListPage's FETCH_CAP.
 * @param {Promise} [opts.started] a startRosterFromRds() promise begun
 *   earlier (concurrently with the Supabase query); started here if absent.
 * @param {AbortSignal} [opts.signal] see startRosterFromRds.
 */
export async function serveRosterFromRds(
  schema,
  { search = "", supabaseRows = [], limit = ROSTER_FETCH_CAP, started, signal } = {},
) {
  return serveFromRds(CUTOVER_PILOT, schema, {
    supabaseRows,
    started: started || startRosterFromRds(schema, { search, limit, signal }),
  });
}

// -- patient_suggest: PatientSuggestionDropdown.js roster search (Phase 4)

/**
 * Kick off the RDS suggestion query for one schema WITHOUT waiting for
 * it (see startFromRds). `serveSuggestionsFromRds` settles it.
 *
 * @param {string} schema tenant schema.
 * @param {object} opts
 * @param {string} opts.query the typed name fragment, exactly as the
 *   dropdown passes it to Supabase's ilike (no trimming here either, so
 *   both sides ILIKE the same value).
 * @param {number} [opts.limit] per-tenant cap (the dropdown's 10).
 * @param {AbortSignal} [opts.signal] aborts the RDS request.
 */
export function startSuggestionsFromRds(schema, { query = "", limit = SUGGEST_LIMIT, signal } = {}) {
  const term = String(query || "");
  if (!term) {
    // The dropdown never searches an empty term (2-char minimum); RDS
    // would answer the whole roster, which is not the same question.
    return Promise.resolve({ skip: true, reason: "empty_search" });
  }
  // Same shape as the dropdown's Supabase query: patient_name ILIKE
  // %query%, 10 rows, patient_name asc (the dropdown orders too, so the
  // two sides pick the same 10 when more match).
  const body = {
    limit,
    order: { column: "patient_name", ascending: true, nulls_first: false },
    search: { columns: SUGGEST_SEARCH_COLUMNS, value: term },
  };
  return startFromRds(SUGGEST_PILOT, schema, body, signal);
}

/**
 * Serve one schema's roster suggestions from RDS (see serveFromRds for
 * the contract; reported under pilot "patient_suggest").
 *
 * @param {string} schema tenant schema the Supabase query ran against.
 * @param {object} opts
 * @param {string} [opts.query] the typed name fragment (see
 *   startSuggestionsFromRds).
 * @param {object[]} [opts.supabaseRows] the roster rows Supabase returned
 *   for this schema (raw, before the dropdown's _schema/_source tags).
 * @param {number} [opts.limit] per-tenant cap (the dropdown's 10).
 * @param {Promise} [opts.started] a startSuggestionsFromRds() promise
 *   begun earlier; started here if absent.
 * @param {AbortSignal} [opts.signal] see startSuggestionsFromRds.
 */
export async function serveSuggestionsFromRds(
  schema,
  { query = "", supabaseRows = [], limit = SUGGEST_LIMIT, started, signal } = {},
) {
  return serveFromRds(SUGGEST_PILOT, schema, {
    supabaseRows,
    started: started || startSuggestionsFromRds(schema, { query, limit, signal }),
  });
}

// -- patient_table: PatientTableViewer.js whole-roster view (Phase 4) ----

/**
 * Kick off the RDS whole-roster query for one schema WITHOUT waiting for
 * it (see startFromRds). `servePatientTableFromRds` settles it.
 *
 * @param {string} schema tenant schema.
 * @param {object} opts
 * @param {number} [opts.limit] row cap (PostgREST's default max-rows).
 * @param {AbortSignal} [opts.signal] aborts the RDS request (the viewer
 *   unmounted or refreshed).
 */
export function startPatientTableFromRds(schema, { limit = TABLE_FETCH_CAP, signal } = {}) {
  // Same shape as the viewer's Supabase query: every row, created_at
  // descending with Postgres's default null placement (the viewer passes
  // no nullsFirst, so neither does this), no search.
  const body = {
    limit,
    order: { column: "created_at", ascending: false },
  };
  return startFromRds(TABLE_PILOT, schema, body, signal);
}

/**
 * Serve one schema's whole roster from RDS (see serveFromRds for the
 * contract; reported under pilot "patient_table").
 *
 * @param {string} schema tenant schema the Supabase query ran against.
 * @param {object} opts
 * @param {object[]} [opts.supabaseRows] the rows Supabase returned.
 * @param {number} [opts.limit] see startPatientTableFromRds.
 * @param {Promise} [opts.started] a startPatientTableFromRds() promise
 *   begun earlier; started here if absent.
 * @param {AbortSignal} [opts.signal] see startPatientTableFromRds.
 */
export async function servePatientTableFromRds(
  schema,
  { supabaseRows = [], limit = TABLE_FETCH_CAP, started, signal } = {},
) {
  return serveFromRds(TABLE_PILOT, schema, {
    supabaseRows,
    started: started || startPatientTableFromRds(schema, { limit, signal }),
  });
}

// -- roster_visits: rosterVisitLookup.js notes-c visit counts (Phase 4) --

/**
 * Kick off the RDS visit-rows query for one chunk of vita_ids WITHOUT
 * waiting for it (see startFromRds). `serveVisitCountsFromRds` settles it.
 *
 * @param {string} schema tenant schema.
 * @param {object} opts
 * @param {string[]} opts.vitaIds the chunk (<= 200, already deduplicated
 *   and blank-free by the caller; done again here for safety).
 * @param {AbortSignal} [opts.signal] aborts the RDS request.
 */
export function startVisitCountsFromRds(schema, { vitaIds = [], signal } = {}) {
  const ids = [...new Set((vitaIds || []).filter(Boolean).map(String))];
  if (ids.length === 0) {
    return Promise.resolve({ skip: true, reason: "no_ids" });
  }
  if (ids.length > VISITS_CHUNK) {
    // RDS caps an IN list at 200 (400 otherwise): the caller chunks, so this
    // is a programming error, not a round trip worth paying for.
    return Promise.resolve({ skip: true, reason: "chunk_too_large" });
  }
  // Same question as `.select('id, vita_id').in('vita_id', chunk)`: no
  // order (the caller only counts), no search.
  const body = {
    limit: VISITS_FETCH_CAP,
    columns: ["id", "vita_id"],
    filters: [{ column: "vita_id", op: "in", value: ids }],
  };
  return startFromRds(VISITS_PILOT, schema, body, signal);
}

/**
 * Serve one chunk's notes-c visit rows from RDS (see serveFromRds for the
 * contract; reported under pilot "roster_visits", one report per chunk).
 * The rows carry id + vita_id only; the caller counts them per vita_id.
 *
 * @param {string} schema tenant schema the Supabase query ran against.
 * @param {object} opts
 * @param {string[]} [opts.vitaIds] the chunk (see startVisitCountsFromRds).
 * @param {object[]} [opts.supabaseRows] the rows Supabase returned for the
 *   chunk (id + vita_id).
 * @param {Promise} [opts.started] a startVisitCountsFromRds() promise
 *   begun earlier; started here if absent.
 * @param {AbortSignal} [opts.signal] see startVisitCountsFromRds.
 */
export async function serveVisitCountsFromRds(
  schema,
  { vitaIds = [], supabaseRows = [], started, signal } = {},
) {
  return serveFromRds(VISITS_PILOT, schema, {
    supabaseRows,
    started: started || startVisitCountsFromRds(schema, { vitaIds, signal }),
  });
}

// -- latest_visit: PatientListPage.openLatestVisit (Phase 4) ------------

/**
 * Kick off the RDS latest-visit row query for one schema WITHOUT waiting
 * for it (see startFromRds). `serveLatestVisitFromRds` settles it.
 *
 * @param {string} schema tenant schema.
 * @param {object} opts
 * @param {string} [opts.vitaId] the patient's vita_id (NOT NULL on both
 *   patient tables; the caller already vetted that).
 * @param {AbortSignal} [opts.signal] aborts the RDS request (the page
 *   unmounted between click and settle).
 */
export function startLatestVisitFromRds(schema, { vitaId, signal } = {}) {
  const id = String(vitaId || "").trim();
  if (!id) {
    return Promise.resolve({ skip: true, reason: "no_vita_id" });
  }
  // Same question as `.select('id, date_, patient_name, EHR_ID').eq('vita_id',
  // ...).order('date_', ascending: false).limit(1)`: vita_id eq, date_ desc
  // with Postgres's default null placement (the page passes none either),
  // limit 1, projection so the full-set comparison works row by row.
  const body = {
    limit: 1,
    order: { column: "date_", ascending: false },
    filters: [{ column: "vita_id", op: "eq", value: id }],
    columns: LATEST_VISIT_COLUMNS,
  };
  return startFromRds(LATEST_VISIT_PILOT, schema, body, signal);
}

/**
 * Serve one schema's latest-visit row from RDS (see serveFromRds for the
 * contract; reported under pilot "latest_visit", one report per click).
 *
 * @param {string} schema tenant schema the Supabase query ran against.
 * @param {object} opts
 * @param {string} [opts.vitaId] see startLatestVisitFromRds.
 * @param {object[]} [opts.supabaseRows] the rows Supabase returned
 *   (zero or one).
 * @param {Promise} [opts.started] a startLatestVisitFromRds() promise
 *   begun earlier; started here if absent.
 * @param {AbortSignal} [opts.signal] see startLatestVisitFromRds.
 */
export async function serveLatestVisitFromRds(
  schema,
  { vitaId, supabaseRows = [], started, signal } = {},
) {
  return serveFromRds(LATEST_VISIT_PILOT, schema, {
    supabaseRows,
    started: started || startLatestVisitFromRds(schema, { vitaId, signal }),
  });
}

// -- prior_visit_match: priorVisitMatch.js earlier-visit list (Phase 4) --

/**
 * Kick off the RDS prior-visit query for one schema, one key WITHOUT
 * waiting for it (see startFromRds). `servePriorVisitMatchFromRds`
 * settles it. The caller issues one call per key in {patient_name,
 * EHR_ID} and unions the answers; this helper is the per-key transport.
 *
 * @param {string} schema tenant schema.
 * @param {object} opts
 * @param {{column: string, value: string}} opts.key one of
 *   `priorVisitQueryKeys`'s output -- the column the caller wants RDS to
 *   filter on (always a text column for this site).
 * @param {string} [opts.before] ISO date string; RDS filters to
 *   date_ < before. Falsy = local skip.
 * @param {number} [opts.limit] row cap (PRIOR_VISIT_ROW_LIMIT = 30 at
 *   the caller; the default matches it).
 * @param {AbortSignal} [opts.signal] aborts the RDS request.
 */
export function startPriorVisitMatchFromRds(schema, { key, before, limit = PRIOR_VISIT_FETCH_CAP, signal } = {}) {
  if (!key || !key.column || key.value === undefined || key.value === null || key.value === "") {
    return Promise.resolve({ skip: true, reason: "no_key" });
  }
  if (!before) {
    return Promise.resolve({ skip: true, reason: "no_before" });
  }
  // Same question as `.select('id, EHR_ID, patient_name, date_, type')
  //   .eq(column, value).lt('date_', before).order('date_', ascending: false)
  //   .limit(30)`: the same five columns, the same two filters, the same
  //   sort, the same cap. The caller's projection stays the same.
  const body = {
    limit,
    order: { column: "date_", ascending: false },
    filters: [
      { column: key.column, op: "eq", value: String(key.value) },
      { column: "date_", op: "lt", value: before },
    ],
    columns: PRIOR_VISIT_COLUMNS,
  };
  return startFromRds(PRIOR_VISIT_PILOT, schema, body, signal);
}

/**
 * Serve one schema's prior-visit rows for one key from RDS (see
 * serveFromRds for the contract; reported under pilot
 * "prior_visit_match", one report per key per call). Off or RDS error
 * = the Supabase rows, unchanged.
 *
 * @param {string} schema tenant schema the Supabase query ran against.
 * @param {object} opts
 * @param {{column: string, value: string}} [opts.key] see
 *   startPriorVisitMatchFromRds.
 * @param {string} [opts.before] see startPriorVisitMatchFromRds.
 * @param {object[]} [opts.supabaseRows] the rows Supabase returned.
 * @param {Promise} [opts.started] a startPriorVisitMatchFromRds()
 *   promise begun earlier; started here if absent.
 * @param {AbortSignal} [opts.signal] see startPriorVisitMatchFromRds.
 */
export async function servePriorVisitMatchFromRds(
  schema,
  { key, before, supabaseRows = [], started, signal } = {},
) {
  return serveFromRds(PRIOR_VISIT_PILOT, schema, {
    supabaseRows,
    started: started || startPriorVisitMatchFromRds(schema, { key, before, signal }),
  });
}

// -- suggestion_visits: PatientSuggestionDropdown visits half (Phase 4) --

/**
 * Kick off the RDS suggestion-visits query for one schema, one variant
 * (past or any) WITHOUT waiting for it (see startFromRds).
 * `serveSuggestionVisitsFromRds` settles it. The caller issues one call
 * per variant per schema (two variants: `includeFuture: false` adds
 * `date_ lt refDate`; `includeFuture: true` keeps all dates with nulls
 * last).
 *
 * @param {string} schema tenant schema.
 * @param {object} opts
 * @param {string} [opts.query] the typed name fragment, exactly as the
 *   caller's Supabase read receives it. Falsy = local skip.
 * @param {string} [opts.refDate] ISO date string. Ignored when
 *   `includeFuture` is true.
 * @param {boolean} [opts.includeFuture] false -> add `date_ lt refDate`
 *   (the past variant); true -> no date filter and `nulls_first: false`.
 * @param {AbortSignal} [opts.signal] aborts the RDS request.
 */
export function startSuggestionVisitsFromRds(
  schema,
  { query, refDate, includeFuture, signal } = {},
) {
  const term = String(query || "");
  if (!term.trim()) {
    return Promise.resolve({ skip: true, reason: "no_query" });
  }
  // Same question as `.select("*").ilike(nameFieldKey, `%${query}%`)`
  //   plus (past) `.lt("date_", refDate).order("date_", ascending: false).limit(15)`
  //   plus (any)  `.order("date_", ascending: false, nullsFirst: false).limit(15)`.
  // The ilike pattern is the caller's literal value with `*` read as `%`
  // (PostgREST parity). Bound as a parameter, not inlined.
  // Same wildcard shape the dropdown builds (`%${query}%`, untrimmed);
  // `*` reads as `%` like PostgREST. The past variant passes no
  // nulls_first, exactly like the Supabase read (Postgres default).
  const order = { column: "date_", ascending: false };
  if (includeFuture) order.nulls_first = false;
  const body = {
    limit: SUGGEST_VISITS_LIMIT,
    order,
    columns: SUGGEST_VISITS_COLUMNS,
    filters: [{ column: "patient_name", op: "ilike", value: "%" + term.replace(/\*/g, "%") + "%" }],
  };
  if (!includeFuture && refDate) {
    body.filters.push({ column: "date_", op: "lt", value: refDate });
  }
  return startFromRds(SUGGEST_VISITS_PILOT, schema, body, signal);
}

/**
 * Serve one schema's suggestion-visits rows for one variant from RDS (see
 * serveFromRds for the contract; reported under pilot
 * "suggestion_visits", one report per variant per call). Off or RDS
 * error = the Supabase rows, unchanged.
 *
 * @param {string} schema tenant schema the Supabase query ran against.
 * @param {object} opts
 * @param {string} [opts.query] see startSuggestionVisitsFromRds.
 * @param {string} [opts.refDate] see startSuggestionVisitsFromRds.
 * @param {boolean} [opts.includeFuture] see startSuggestionVisitsFromRds.
 * @param {object[]} [opts.supabaseRows] the rows Supabase returned.
 * @param {Promise} [opts.started] a startSuggestionVisitsFromRds()
 *   promise begun earlier; started here if absent.
 * @param {AbortSignal} [opts.signal] see startSuggestionVisitsFromRds.
 */
export async function serveSuggestionVisitsFromRds(
  schema,
  { query, refDate, includeFuture, supabaseRows = [], started, signal } = {},
) {
  return serveFromRds(SUGGEST_VISITS_PILOT, schema, {
    supabaseRows,
    started: started || startSuggestionVisitsFromRds(schema, { query, refDate, includeFuture, signal }),
  });
}

// -- search_visits / search_roster: SearchComponent.js (Phase 4) -------

/** The ILIKE filters SearchComponent's applyContainsFilters would send,
 * as /query filters: one per term, `%term%`, name fields per word. */
function searchContainsFilters(pairs) {
  const out = [];
  (pairs || []).forEach(([column, value]) => {
    containsFilterTerms(column, value).forEach((term) => {
      out.push({ column, op: "ilike", value: "%" + String(term).replace(/\*/g, "%") + "%" });
    });
  });
  return out;
}

/**
 * Kick off the RDS /search visits query for one schema WITHOUT waiting
 * for it (see startFromRds). `serveSearchVisitsFromRds` settles it.
 *
 * @param {string} schema tenant schema.
 * @param {object} opts
 * @param {object} [opts.searchValues] the typed fields, keyed by notes-c
 *   column, exactly as SearchComponent holds them.
 * @param {string} [opts.startDate] / @param {string} [opts.endDate] the
 *   date range: one of them = `date_ eq`, both = `gte` + `lte`, neither =
 *   no date filter -- the same three cases the Supabase read builds.
 * @param {AbortSignal} [opts.signal] aborts the RDS request.
 */
export function startSearchVisitsFromRds(schema, { searchValues = {}, startDate = "", endDate = "", signal } = {}) {
  const filters = searchContainsFilters(Object.entries(searchValues || {}));
  if (startDate && endDate) {
    filters.push({ column: "date_", op: "gte", value: startDate });
    filters.push({ column: "date_", op: "lte", value: endDate });
  } else if (startDate || endDate) {
    filters.push({ column: "date_", op: "eq", value: endDate || startDate });
  }
  if (filters.length > SEARCH_MAX_FILTERS) {
    return Promise.resolve({ skip: true, reason: "too_many_filters" });
  }
  // The Supabase read also scopes non-clinical roles to their own / shared
  // rows with an .or() on user_id / shared_users; RDS applies the same
  // rule server-side (rds_access.resolve) to every row it returns, so no
  // filter is sent for it.
  const body = { limit: SEARCH_FETCH_CAP, order: { column: "date_", ascending: false } };
  if (filters.length) body.filters = filters;
  return startFromRds(SEARCH_VISITS_PILOT, schema, body, signal);
}

/** Serve one schema's /search visit rows from RDS (see serveFromRds). */
export async function serveSearchVisitsFromRds(
  schema,
  { searchValues, startDate, endDate, supabaseRows = [], started, signal } = {},
) {
  return serveFromRds(SEARCH_VISITS_PILOT, schema, {
    supabaseRows,
    started: started || startSearchVisitsFromRds(schema, { searchValues, startDate, endDate, signal }),
  });
}

/**
 * Kick off the RDS /search roster query for one schema WITHOUT waiting
 * for it. `filters` is planRosterSearch's output: [[rosterColumn, value]].
 */
export function startSearchRosterFromRds(schema, { filters = [], signal } = {}) {
  const rdsFilters = searchContainsFilters(filters);
  if (rdsFilters.length > SEARCH_MAX_FILTERS) {
    return Promise.resolve({ skip: true, reason: "too_many_filters" });
  }
  const body = {
    limit: SEARCH_FETCH_CAP,
    order: { column: "patient_name", ascending: true, nulls_first: false },
  };
  if (rdsFilters.length) body.filters = rdsFilters;
  return startFromRds(SEARCH_ROSTER_PILOT, schema, body, signal);
}

/** Serve one schema's /search roster rows from RDS (see serveFromRds). */
export async function serveSearchRosterFromRds(
  schema,
  { filters, supabaseRows = [], started, signal } = {},
) {
  return serveFromRds(SEARCH_ROSTER_PILOT, schema, {
    supabaseRows,
    started: started || startSearchRosterFromRds(schema, { filters, signal }),
  });
}

// -- chart_roster / chart_visit: usePatientDocument.js (Phase 4) ---------

/** Kick off the RDS roster chart-row read (patients_with_ehrid, id eq). */
export function startChartRosterFromRds(schema, { id, signal } = {}) {
  const key = String(id || "").trim();
  if (!key) return Promise.resolve({ skip: true, reason: "no_id" });
  return startFromRds(CHART_ROSTER_PILOT, schema, {
    limit: 1,
    filters: [{ column: "id", op: "eq", value: key }],
  }, signal);
}

/** Serve the roster chart row from RDS (see serveFromRds). */
export async function serveChartRosterFromRds(schema, { id, supabaseRows = [], started, signal } = {}) {
  return serveFromRds(CHART_ROSTER_PILOT, schema, {
    supabaseRows,
    started: started || startChartRosterFromRds(schema, { id, signal }),
  });
}

/**
 * Kick off the RDS visit chart-row read (notes-c, `column` eq `value`,
 * column being id or EHR_ID -- the same choice usePatientDocument makes
 * from the route param). limit 2: one row is the answer, two is the
 * ambiguity .single() would have refused, and the caller keeps
 * Supabase's row in that case.
 */
export function startChartVisitFromRds(schema, { column, value, signal } = {}) {
  if (!CHART_VISIT_KEY_COLUMNS.includes(column)) {
    return Promise.resolve({ skip: true, reason: "bad_column" });
  }
  const key = String(value || "").trim();
  if (!key) return Promise.resolve({ skip: true, reason: "no_key" });
  return startFromRds(CHART_VISIT_PILOT, schema, {
    limit: 2,
    filters: [{ column, op: "eq", value: key }],
  }, signal);
}

/** Serve the visit chart row from RDS (see serveFromRds). */
export async function serveChartVisitFromRds(
  schema,
  { column, value, supabaseRows = [], started, signal } = {},
) {
  return serveFromRds(CHART_VISIT_PILOT, schema, {
    supabaseRows,
    started: started || startChartVisitFromRds(schema, { column, value, signal }),
  });
}

// -- prior_visits_modal: PriorVisitsModal.js (Phase 4) -------------------

/**
 * Kick off the RDS history-list read: notes-c, date_ <= today, keyed by
 * EHR_ID when the chart has one else by patient_name, newest first, 200
 * rows, the modal's ten columns -- the Supabase query verbatim.
 */
export function startPriorVisitsModalFromRds(schema, { ehrId, patientName, todayIso, signal } = {}) {
  const day = String(todayIso || "").trim();
  if (!day) return Promise.resolve({ skip: true, reason: "no_date" });
  const key = String(ehrId || "").trim()
    ? { column: "EHR_ID", op: "eq", value: String(ehrId).trim() }
    : String(patientName || "").trim()
      ? { column: "patient_name", op: "eq", value: String(patientName) }
      : null;
  if (!key) return Promise.resolve({ skip: true, reason: "no_key" });
  return startFromRds(PRIOR_VISITS_MODAL_PILOT, schema, {
    limit: PRIOR_VISITS_MODAL_LIMIT,
    order: { column: "date_", ascending: false },
    columns: PRIOR_VISITS_MODAL_COLUMNS,
    filters: [{ column: "date_", op: "lte", value: day }, key],
  }, signal);
}

/** Serve the history list from RDS (see serveFromRds). */
export async function servePriorVisitsModalFromRds(
  schema,
  { ehrId, patientName, todayIso, supabaseRows = [], started, signal } = {},
) {
  return serveFromRds(PRIOR_VISITS_MODAL_PILOT, schema, {
    supabaseRows,
    started: started || startPriorVisitsModalFromRds(schema, { ehrId, patientName, todayIso, signal }),
  });
}

// -- admin_patients: SuperAdmin.js patients modal (Phase 4) --------------

/** Kick off the RDS whole-table read of `patients` (1000 cap, no order:
 * the modal never sorted either; rows are compared by id). */
export function startAdminPatientsFromRds(schema, { signal } = {}) {
  return startFromRds(ADMIN_PATIENTS_PILOT, schema, { limit: ADMIN_PATIENTS_FETCH_CAP }, signal);
}

/** Serve the admin patients list from RDS (see serveFromRds). */
export async function serveAdminPatientsFromRds(schema, { supabaseRows = [], started, signal } = {}) {
  return serveFromRds(ADMIN_PATIENTS_PILOT, schema, {
    supabaseRows,
    started: started || startAdminPatientsFromRds(schema, { signal }),
  });
}

// -- Phase D: mirroring a succeeded Supabase write to RDS -----------------

// Write sites (TEST_INTEGRATION_PLAN.md §25). Supabase stays the source of
// truth; the point of the mirror write is that RDS holds the row NOW
// instead of at the next scheduled sync, so a read cutover on the same
// table does not lag the user's own write by up to five minutes. The sync
// still runs and reconciles (upsert by id; prune of rows the source lacks),
// so a mirror write that fails, or lands without its Supabase twin, is
// self-healing.
const WRITE_SITES = {
  write_patients: { table: "patients" },
  write_visit_template: { table: "notes-c" },
};

/**
 * Does the PHI write overlay (rdsPhiOverlay.js, §31 7.3) already forward
 * every mutation on `table`? When it does, a per-site mirror write would
 * send the same row a second time (and report it twice), so
 * mirrorWriteToRds stands down. The require is deferred: rdsPhiOverlay
 * is loaded by supabaseClient.js, which this module imports, and a
 * top-level import would close that cycle (§31 ops #11). An overlay
 * build without writeOverlayActive (or one that throws) counts as "not
 * active" -- today's behaviour.
 */
async function writeOverlayHandles(table) {
  try {
    const { writeOverlayActive } = require("./rdsPhiOverlay");
    if (typeof writeOverlayActive !== "function") return false;
    return (await writeOverlayActive(table)) === true;
  } catch (_) {
    return false;
  }
}

/**
 * Mirror `row` (Supabase's already-written row, or the id plus the changed
 * columns) to RDS for `site`. Never throws, never awaits anything the
 * caller must wait for -- the Supabase write already succeeded; this is
 * best-effort freshness plus a dashboard report. Resolves to
 *   { served: "rds" }                          RDS took the row;
 *   { served: "supabase_fallback", reason }    RDS did not (reported);
 *   { served: "supabase_only", reason }        not attempted: flag off,
 *                                              no id, unknown site, or
 *                                              the write overlay already
 *                                              forwarded it (§31:
 *                                              "overlay_active", not
 *                                              reported -- the overlay
 *                                              reports its own outcome).
 */
export async function mirrorWriteToRds(site, schema, row) {
  const spec = WRITE_SITES[site];
  if (!spec) return { served: "supabase_only", reason: "unknown_site" };
  if (await writeOverlayHandles(spec.table)) {
    return { served: "supabase_only", reason: "overlay_active" };
  }
  if (!row || typeof row !== "object" || row.id === undefined || row.id === null) {
    return { served: "supabase_only", reason: "no_id" };
  }
  if (!(await isCutoverEffective(site))) return { served: "supabase_only", reason: "flag_off" };
  const base = {
    pilot: site, tenant: schema, table: spec.table, sample_size: 1,
    columns: Object.keys(row).filter((k) => !isIgnoredKey(k)).sort(),
  };
  const fallback = (reason, fields) => {
    reportCutoverOutcome({ ...base, served: "supabase_fallback", reason, ...fields });
    return { served: "supabase_fallback", reason };
  };
  if (circuitOpen()) return fallback("circuit_open", { outcome: "skipped" });
  try {
    await writeRow(schema, spec.table, row);
  } catch (err) {
    if (err instanceof PhiClientError) {
      return fallback(`PhiClientError ${err.statusCode}`, {
        outcome: err.statusCode === 404 ? "rds_not_mirrored" : "rds_error",
        status_code: err.statusCode,
        detail: typeof err.detail === "string" ? err.detail : "non-string detail omitted",
      });
    }
    if (err instanceof PhiClientUnavailable) {
      const reason = unavailableReason(err);
      if (reason === "timeout" || reason === "unreachable") tripCircuit();
      return fallback(reason, { outcome: "rds_unavailable" });
    }
    return fallback(err?.name || "Error", { outcome: "unexpected_error" });
  }
  reportCutoverOutcome({ ...base, served: "rds", outcome: "ok" });
  return { served: "rds", reason: null };
}

